import React, { useState, useEffect } from 'react';
import './MeetingNotesModal.css';

function MeetingNotesModal({
  isOpen,
  onClose,
  isLoading,
  meetingNotes,
  meetingDate,
  employeeName,
  meetingLink,
  scheduledBy,
  cycleId,
  employeeEmail,
  managerEmail,
  onSave
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Update edited notes when meeting notes change
  useEffect(() => {
    if (meetingNotes) {
      // Try to parse JSON and convert to readable format
      try {
        const parsed = JSON.parse(meetingNotes);
        if (parsed.summary && parsed.action_items) {
          // Convert structured JSON to readable text format WITHOUT headings
          let readableText = `${parsed.summary}\n\n`;
          if (parsed.action_items && parsed.action_items.length > 0) {
            parsed.action_items.forEach((item, index) => {
              readableText += `${index + 1}. ${item}\n`;
            });
          }
          setEditedNotes(readableText);
        } else {
          setEditedNotes(meetingNotes);
        }
      } catch (e) {
        // Not JSON, use as is
        setEditedNotes(meetingNotes);
      }
    } else {
      setEditedNotes('');
    }
  }, [meetingNotes]);

  const handleSaveNotes = async () => {
    try {
      setIsSaving(true);
      
      // Convert readable text back to JSON format if it was originally JSON
      let notesToSave = editedNotes;
      const parsedOriginal = parseMeetingNotes(meetingNotes);
      
      if (parsedOriginal) {
        // Split by double newline to separate summary from action items
        const parts = editedNotes.split('\n\n');
        const summary = parts[0]?.trim() || 'No summary provided';
        
        // Everything after first double newline is action items
        const actionItemsText = parts.slice(1).join('\n\n');
        const actionItems = [];
        
        if (actionItemsText) {
          const lines = actionItemsText.split('\n').filter(line => line.trim());
          for (let line of lines) {
            // Remove numbering (e.g., "1. ", "2. ")
            const cleanedItem = line.replace(/^\d+\.\s*/, '').trim();
            if (cleanedItem) {
              actionItems.push(cleanedItem);
            }
          }
        }
        
        // Convert back to JSON format
        notesToSave = JSON.stringify({
          summary: summary,
          action_items: actionItems
        });
      }
      
      await onSave(cycleId, employeeEmail, managerEmail, notesToSave);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving meeting notes:', error);
      // Error snackbar is shown by the parent component
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    // Reset to original format
    if (meetingNotes) {
      try {
        const parsed = JSON.parse(meetingNotes);
        if (parsed.summary && parsed.action_items) {
          let readableText = `${parsed.summary}\n\n`;
          if (parsed.action_items && parsed.action_items.length > 0) {
            parsed.action_items.forEach((item, index) => {
              readableText += `${index + 1}. ${item}\n`;
            });
          }
          setEditedNotes(readableText);
        } else {
          setEditedNotes(meetingNotes);
        }
      } catch (e) {
        setEditedNotes(meetingNotes);
      }
    } else {
      setEditedNotes('');
    }
    setIsEditing(false);
  };

  if (!isOpen) return null;

  // Format the meeting date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Parse meeting notes if it's JSON format (from AI processing)
  const parseMeetingNotes = (notes) => {
    if (!notes) return null;
    
    try {
      const parsed = JSON.parse(notes);
      if (parsed.summary && parsed.action_items) {
        return parsed;
      }
    } catch (e) {
      // Not JSON, treat as plain text
    }
    return null;
  };

  const parsedNotes = parseMeetingNotes(meetingNotes);
  const isStructuredNotes = parsedNotes !== null;

  return (
    <div className="meeting-notes-modal-overlay" onClick={onClose}>
      <div className="meeting-notes-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="meeting-notes-modal-header">
          <div className="meeting-notes-header-info">
            <div className="meeting-notes-title-row">
              <h2>Meeting Notes</h2>
            </div>
            <p className="meeting-info">
              <span><strong>Employee:</strong> {employeeName}</span>
              <span className="info-separator">|</span>
              <span><strong>Date:</strong> {formatDate(meetingDate)}</span>
            </p>
            {scheduledBy && (
              <p className="meeting-info scheduled-by">
                <strong>Scheduled by:</strong> {scheduledBy}
              </p>
            )}
          </div>
          <button className="meeting-notes-close-button" onClick={onClose}>×</button>
        </div>

        <div className="meeting-notes-content">
          {isLoading ? (
            <div className="meeting-notes-loading">
              <div className="loading-spinner"></div>
              <p>Loading meeting notes...</p>
            </div>
          ) : isEditing ? (
            <div className="meeting-notes-edit">
              <div className="edit-section">
                <h3 className="edit-heading">Summary</h3>
                <textarea
                  value={editedNotes.split('\n\n')[0] || ''}
                  onChange={(e) => {
                    const parts = editedNotes.split('\n\n');
                    parts[0] = e.target.value;
                    setEditedNotes(parts.join('\n\n'));
                  }}
                  placeholder="Enter summary..."
                  className="meeting-notes-textarea"
                  rows={5}
                  disabled={isSaving}
                />
              </div>
              <div className="edit-section">
                <h3 className="edit-heading">Action Items</h3>
                <textarea
                  value={editedNotes.split('\n\n').slice(1).join('\n\n') || ''}
                  onChange={(e) => {
                    const parts = editedNotes.split('\n\n');
                    const summary = parts[0] || '';
                    setEditedNotes(summary + '\n\n' + e.target.value);
                  }}
                  placeholder="Enter action items (one per line, e.g., 1. First item)..."
                  className="meeting-notes-textarea"
                  rows={5}
                  disabled={isSaving}
                />
              </div>
            </div>
          ) : isStructuredNotes ? (
            <div className="meeting-notes-structured">
              <div className="summary-section">
                <h3>Summary</h3>
                <div className="summary-content">
                  {parsedNotes.summary}
                </div>
              </div>
              
              <div className="action-items-section">
                <h3>Action Items</h3>
                {parsedNotes.action_items && parsedNotes.action_items.length > 0 ? (
                  <div className="action-items-list">
                    <ul>
                      {parsedNotes.action_items.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                    <div className="action-items-organizer">
                      <small>Organized by: {scheduledBy || 'Unknown'}</small>
                    </div>
                  </div>
                ) : (
                  <p className="no-action-items">No action items identified in this meeting.</p>
                )}
              </div>
            </div>
          ) : (
            <div className="meeting-notes-text">
              {meetingNotes || 'No meeting notes available.'}
            </div>
          )}
        </div>

        <div className="meeting-notes-modal-actions">
          {isLoading ? (
            <button className="meeting-notes-close-btn" onClick={onClose}>Close</button>
          ) : isEditing ? (
            <>
              <button
                className="meeting-notes-save-btn"
                onClick={handleSaveNotes}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Notes'}
              </button>
              <button
                className="meeting-notes-cancel-btn"
                onClick={handleCancelEdit}
                disabled={isSaving}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                className="meeting-notes-add-btn"
                onClick={() => setIsEditing(true)}
              >
                {meetingNotes ? 'Edit Meeting Notes' : 'Add Meeting Notes'}
              </button>
              <button className="meeting-notes-close-btn" onClick={onClose}>Close</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MeetingNotesModal;